import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface OriginalContext {
  research_context?: string;
  transcript?: string;
  engineer_notes?: string;
  wind_report?: unknown;
}

export interface ReviewDraftContextValue {
  versions: Record<string, unknown>[];
  activeVersionIndex: number;
  setActiveVersionIndex: (index: number) => void;
  addVersion: (draft: Record<string, unknown>) => void;
  setVersionsFromInitial: (initial: Record<string, unknown>, originalContext?: OriginalContext) => void;
  updateVersionAt: (index: number, draft: Record<string, unknown>) => void;
  originalContext: OriginalContext;
}

const ReviewDraftContext = createContext<ReviewDraftContextValue | undefined>(undefined);

export function useReviewDraft() {
  const ctx = useContext(ReviewDraftContext);
  return ctx;
}

interface ReviewDraftProviderProps {
  children: ReactNode;
}

export function ReviewDraftProvider({ children }: ReviewDraftProviderProps) {
  const [versions, setVersions] = useState<Record<string, unknown>[]>([]);
  const [activeVersionIndex, setActiveVersionIndex] = useState(0);
  const [originalContext, setOriginalContext] = useState<OriginalContext>({});

  const setVersionsFromInitial = useCallback((initial: Record<string, unknown>, originalContextArg?: OriginalContext) => {
    setVersions([{ ...initial }]);
    setActiveVersionIndex(0);
    setOriginalContext(originalContextArg || {});
  }, []);

  const addVersion = useCallback((draft: Record<string, unknown>) => {
    setVersions((prev) => [...prev, { ...draft }]);
    setActiveVersionIndex((prev) => prev + 1);
  }, []);

  const updateVersionAt = useCallback((index: number, draft: Record<string, unknown>) => {
    setVersions((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = { ...draft };
      return next;
    });
  }, []);

  const value: ReviewDraftContextValue = {
    versions,
    activeVersionIndex,
    setActiveVersionIndex,
    addVersion,
    setVersionsFromInitial,
    updateVersionAt,
    originalContext,
  };

  return (
    <ReviewDraftContext.Provider value={value}>
      {children}
    </ReviewDraftContext.Provider>
  );
}
